import React, { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { ChevronDown, Copy } from 'lucide-react';

type PaletteInput =
  | string
  | {
      colors: string[];
      inspiration?: string;
    };

interface ColorPaletteResponseProps {
  palette: PaletteInput;
  isDarkMode: boolean;
}

const hexToRgb = (hex: string): string => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 'rgb(0, 0, 0)';
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgb(${r}, ${g}, ${b})`;
};

const hexToHsl = (hex: string): string => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return 'hsl(0, 0%, 0%)';
  let r = parseInt(m[1], 16) / 255;
  let g = parseInt(m[2], 16) / 255;
  let b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
};

export const ColorPaletteResponse: React.FC<ColorPaletteResponseProps> = ({ palette, isDarkMode }) => {
  const { toast } = useToast();
  const [showFormats, setShowFormats] = useState(false);

  const { colors, inspiration } = useMemo(() => {
    if (typeof palette === 'string') {
      const matched = palette.match(/#[0-9A-Fa-f]{6}/g) || [];
      return { colors: matched.slice(0, 9), inspiration: undefined as string | undefined };
    }
    return { colors: (palette.colors || []).slice(0, 9), inspiration: palette.inspiration };
  }, [palette]);

  const copy = (text: string, label?: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied', description: `${label || text} copied to clipboard` });
  };

  if (!colors || colors.length === 0) {
    return (
      <Card className={`p-4 ${isDarkMode ? 'bg-[#1f1f1f]' : 'bg-gray-50'}`}>
        <p className="text-sm">No valid colors found.</p>
      </Card>
    );
  }

  return (
    <Card className={`p-0 overflow-hidden ${isDarkMode ? 'bg-[#151515] border-[#2a2a2a]' : 'bg-white'}`}>
      <div className="h-48 w-full flex overflow-x-auto">
        {colors.map((c, i) => (
          <button
            key={i}
            className="group relative flex-1 min-w-[84px] flex items-center justify-center text-white font-semibold tracking-wide transition-[flex,box-shadow] duration-150 focus:outline-none"
            style={{ background: c }}
            onClick={() => copy(c, c)}
          >
            <span className="opacity-0 group-hover:opacity-100 transition-opacity text-xs px-2 py-1 rounded bg-black/70">
              {c.replace('#', '').toUpperCase()}
            </span>
          </button>
        ))}
      </div>

      <div className={`h-12 w-full flex items-center justify-between px-4 ${isDarkMode ? 'bg-[#1b1b1b] text-white' : 'bg-white text-gray-900'}`}>
        <span className="text-sm font-medium truncate">{inspiration || 'Creative Harmony'}</span>
        <button
          onClick={() => setShowFormats((s) => !s)}
          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-xl border ${isDarkMode ? 'border-[#2c2c2c] hover:bg-[#222]' : 'border-gray-200 hover:bg-gray-50'}`}
        >
          Formats
          <ChevronDown className={`w-3 h-3 transition-transform ${showFormats ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {showFormats && (
        <div className={`px-4 py-3 text-sm ${isDarkMode ? 'bg-[#1b1b1b] text-gray-200' : 'bg-gray-50 text-gray-800'}`}>
          <div className="font-semibold mb-2">All Color Formats (click to copy)</div>
          <div className="max-h-60 overflow-y-auto pr-1 space-y-2">
            {colors.map((c, idx) => (
              <div key={idx} className={`flex flex-col border-b pb-2 ${isDarkMode ? 'border-[#222]' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm border" style={{ backgroundColor: c }} />
                    <span className="font-medium">HEX</span>
                  </div>
                  <button onClick={() => copy(c, c)} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-black/10 dark:bg-white/10">
                    <Copy className="w-3 h-3" /> {c}
                  </button>
                </div>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm border" style={{ backgroundColor: c }} />
                    <span className="font-medium">RGB</span>
                  </div>
                  <button onClick={() => copy(hexToRgb(c), hexToRgb(c))} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-black/10 dark:bg-white/10">
                    <Copy className="w-3 h-3" /> {hexToRgb(c)}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm border" style={{ backgroundColor: c }} />
                    <span className="font-medium">HSL</span>
                  </div>
                  <button onClick={() => copy(hexToHsl(c), hexToHsl(c))} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-black/10 dark:bg-white/10">
                    <Copy className="w-3 h-3" /> {hexToHsl(c)}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default ColorPaletteResponse;
